import { Breadcrumb, Layout, Spin, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import IWarehousesPortfolioMetrics from '~Api/Warehouse/IWarehousesPortfolioMetrics';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import { IDictionary } from '~utilities/IDictionary';
import { currencyFormatter, percentageFormatter } from '~utilities/formatters';
import { warehousesPortfolioDashboardListAction } from './actions';
import { warehousesPortfolioDashboardSelector } from './selectors';

enum PortfolioMetricEnum {
    TotalFunds = 'TOTAL_FUNDS',
    TotalLoanBook = 'TOTAL_LOAN_BOOK',
    LoanBookPerforming = 'LOAN_BOOK_PERFORMING',
    LoanBookNonPerforming = 'LOAN_BOOK_NON_PERFORMING',
    LoanBookNonPerformingPercentage = 'LOAN_BOOK_NON_PERFORMING_PERCENTAGE',
    TotalReserved = 'TOTAL_RESERVED',
    TotalRetained = 'TOTAL_RETAINED',
    AvailableFunds = 'AVAILABLE_FUNDS',
    UtilisationRate = 'UTILISATION_RATE',
    WeightedLvr = 'WEIGHTED_LVR',
    CostOfFunds = 'COST_OF_FUNDS',
    ActualYield = 'ACTUAL_YIELD',
    NimPerformingPercentage = 'NIM_PERFORMING_PERCENTAGE',
    NimPerformingValue = 'NIM_PERFORMING_VALUE',
    OptimalYield = 'OPTIMAL_YIELD',
    NimTotalPercentage = 'NIM_TOTAL_PERCENTAGE',
    NimTotalValue = 'NIM_TOTAL_VALUE',
}

const metricsLabels: IDictionary<string> = {
    [PortfolioMetricEnum.ActualYield]: 'Cash Yield',
    [PortfolioMetricEnum.AvailableFunds]: 'Available Funds',
    [PortfolioMetricEnum.CostOfFunds]: 'Cost of Funds',
    [PortfolioMetricEnum.LoanBookNonPerforming]: 'Loan Book - Non-Performing',
    [PortfolioMetricEnum.LoanBookNonPerformingPercentage]: 'Loan Book - Non-Performing %',
    [PortfolioMetricEnum.LoanBookPerforming]: 'Loan Book - Performing',
    [PortfolioMetricEnum.NimPerformingPercentage]: 'Cash NIM',
    [PortfolioMetricEnum.NimPerformingValue]: 'Cash Profit / Loss',
    [PortfolioMetricEnum.NimTotalPercentage]: 'Contract NIM',
    [PortfolioMetricEnum.NimTotalValue]: 'Contract Profit / Loss',
    [PortfolioMetricEnum.OptimalYield]: 'Contract Yield',
    [PortfolioMetricEnum.TotalFunds]: 'Total Funds',
    [PortfolioMetricEnum.TotalLoanBook]: 'Loan Book - Total',
    [PortfolioMetricEnum.TotalReserved]: 'Reserved Funds',
    [PortfolioMetricEnum.TotalRetained]: 'Total Retained',
    [PortfolioMetricEnum.UtilisationRate]: 'Utilisation Rate',
    [PortfolioMetricEnum.WeightedLvr]: 'Weighted LVR',
};

interface IRestructuredMetrics {
    metric: PortfolioMetricEnum;
    warehouses: IDictionary<number>;
}

export default function PortfolioDashboard(): ReactElement {
    const warehousesPortfolioMetrics: IDictionary<IWarehousesPortfolioMetrics> = useSelector(warehousesPortfolioDashboardSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        dispatch(warehousesPortfolioDashboardListAction());
    }, [
        dispatch,
    ]);

    const rowFormatter: (row: IRestructuredMetrics, value: number) => string = useCallback((row: IRestructuredMetrics, value: number) => {
        switch (row.metric) {
            case PortfolioMetricEnum.TotalFunds:
            case PortfolioMetricEnum.TotalLoanBook:
            case PortfolioMetricEnum.LoanBookPerforming:
            case PortfolioMetricEnum.LoanBookNonPerforming:
            case PortfolioMetricEnum.TotalReserved:
            case PortfolioMetricEnum.TotalRetained:
            case PortfolioMetricEnum.AvailableFunds:
            case PortfolioMetricEnum.NimPerformingValue:
            case PortfolioMetricEnum.NimTotalValue:
                return currencyFormatter.format(value);
            case PortfolioMetricEnum.CostOfFunds:
            case PortfolioMetricEnum.WeightedLvr:
            case PortfolioMetricEnum.ActualYield:
            case PortfolioMetricEnum.OptimalYield:
            case PortfolioMetricEnum.LoanBookNonPerformingPercentage:
            case PortfolioMetricEnum.UtilisationRate:
            case PortfolioMetricEnum.NimPerformingPercentage:
            case PortfolioMetricEnum.NimTotalPercentage:
                return percentageFormatter.format(value / 100);
        }
    }, []);

    if (!warehousesPortfolioMetrics) {
        return (
            <Layout className='warehouses'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/warehouses'>Warehouses</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Portfolio Dashboard</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Warehouses Portfolio Dashboard</Typography.Title>
                        <Spin/>
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const restructuredPortfolioMetrics: IDictionary<IRestructuredMetrics> = {};
    _.each(PortfolioMetricEnum, (metric: PortfolioMetricEnum) => {
        restructuredPortfolioMetrics[metric] = {
            metric: metric,
            warehouses: {
                [WarehouseTypeEnum.Fin]: 0,
                [WarehouseTypeEnum.Fit]: 0,
                [WarehouseTypeEnum.Fnom]: 0,
                [WarehouseTypeEnum.Fwt1]: 0,
                [WarehouseTypeEnum.Fwt2]: 0,
                ['TOTAL']: 0,
            },
        };
    });

    _.each(warehousesPortfolioMetrics, (metrics: IWarehousesPortfolioMetrics) => {
        restructuredPortfolioMetrics[PortfolioMetricEnum.TotalFunds].warehouses[metrics.warehouseType] = metrics.totalCapital;
        restructuredPortfolioMetrics[PortfolioMetricEnum.TotalLoanBook].warehouses[metrics.warehouseType] = metrics.totalBalancePrincipal;
        restructuredPortfolioMetrics[PortfolioMetricEnum.LoanBookPerforming].warehouses[metrics.warehouseType] = metrics.loanBookPerforming;
        restructuredPortfolioMetrics[PortfolioMetricEnum.LoanBookNonPerforming].warehouses[metrics.warehouseType] = metrics.loanBookNonPerforming;
        restructuredPortfolioMetrics[PortfolioMetricEnum.LoanBookNonPerformingPercentage].warehouses[metrics.warehouseType] = metrics.loanBookNonPerformingPercentage;
        restructuredPortfolioMetrics[PortfolioMetricEnum.TotalRetained].warehouses[metrics.warehouseType] = metrics.totalRetained;
        restructuredPortfolioMetrics[PortfolioMetricEnum.TotalReserved].warehouses[metrics.warehouseType] = metrics.totalReserved;
        restructuredPortfolioMetrics[PortfolioMetricEnum.AvailableFunds].warehouses[metrics.warehouseType] = metrics.availableFunds;
        restructuredPortfolioMetrics[PortfolioMetricEnum.UtilisationRate].warehouses[metrics.warehouseType] = metrics.utilisationRate;
        restructuredPortfolioMetrics[PortfolioMetricEnum.WeightedLvr].warehouses[metrics.warehouseType] = metrics.weightedLvr;
        restructuredPortfolioMetrics[PortfolioMetricEnum.CostOfFunds].warehouses[metrics.warehouseType] = metrics.costOfFunds;
        restructuredPortfolioMetrics[PortfolioMetricEnum.ActualYield].warehouses[metrics.warehouseType] = metrics.actualYield;
        restructuredPortfolioMetrics[PortfolioMetricEnum.NimPerformingValue].warehouses[metrics.warehouseType] = metrics.cashNimValue;
        restructuredPortfolioMetrics[PortfolioMetricEnum.NimPerformingPercentage].warehouses[metrics.warehouseType] = metrics.cashNimPercentage;
        restructuredPortfolioMetrics[PortfolioMetricEnum.OptimalYield].warehouses[metrics.warehouseType] = metrics.optimalYield;
        restructuredPortfolioMetrics[PortfolioMetricEnum.NimTotalValue].warehouses[metrics.warehouseType] = metrics.contractNimValue;
        restructuredPortfolioMetrics[PortfolioMetricEnum.NimTotalPercentage].warehouses[metrics.warehouseType] = metrics.contractNimPercentage;
    });

    const columns: ColumnType<IRestructuredMetrics>[] = [
        {
            className: 'metric-name',
            dataIndex: 'metric',
            render: (metric: PortfolioMetricEnum) => metricsLabels[metric],
            title: '',
        },
        {
            align: 'right',
            render: (row: IRestructuredMetrics) => rowFormatter(row, row.warehouses[WarehouseTypeEnum.Fit]),
            title: WarehouseTypeEnum.Fit,
            width: '14%',
        },
        {
            align: 'right',
            render: (row: IRestructuredMetrics) => rowFormatter(row, row.warehouses[WarehouseTypeEnum.Fin]),
            title: WarehouseTypeEnum.Fin,
            width: '14%',
        },
        {
            align: 'right',
            render: (row: IRestructuredMetrics) => rowFormatter(row, row.warehouses[WarehouseTypeEnum.Fnom]),
            title: WarehouseTypeEnum.Fnom,
            width: '14%',
        },
        {
            align: 'right',
            render: (row: IRestructuredMetrics) => rowFormatter(row, row.warehouses[WarehouseTypeEnum.Fwt1]),
            title: WarehouseTypeEnum.Fwt1,
            width: '14%',
        },
        {
            align: 'right',
            render: (row: IRestructuredMetrics) => rowFormatter(row, row.warehouses[WarehouseTypeEnum.Fwt2]),
            title: WarehouseTypeEnum.Fwt2,
            width: '14%',
        },
        {
            align: 'right',
            render: (row: IRestructuredMetrics) => rowFormatter(row, row.warehouses['TOTAL']),
            title: 'Total Group',
            width: '14%',
        },
    ];
    return (
        <Layout className='warehouses'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/warehouses'>Warehouses</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Portfolio Dashboard</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Typography.Title level={2}>Warehouses Portfolio Dashboard</Typography.Title>
                    <Table
                        columns={columns}
                        className='portfolio-dashboard'
                        dataSource={_.values(restructuredPortfolioMetrics)}
                        pagination={false}
                        rowKey='metric'
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
        </Layout>
    );
}
